import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { BrandMark, Button, Card, ThemeToggle } from '@clari/ui'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { Footer } from '@/components/Footer'
import { useAuthStore } from '@/stores/authStore'

const HOW_IT_WORKS_ID = 'how-it-works'

/**
 * Phase 1 landing — proves the design system, the i18n stack (FR/EN/AR/ES),
 * the RTL switch on Arabic, and the dark-mode-by-default token wiring all
 * work end-to-end.
 *
 * No string is hardcoded in the JSX — every visible piece of copy goes
 * through `t()` from the `common` namespace.
 */
export function Landing() {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  // Same signal as ProtectedRoute: a persisted access token. If it has expired,
  // /app refreshes the session or bounces to /login — never a dead end.
  const signedIn = useAuthStore((s) => s.accessToken !== null)

  const scrollToHowItWorks = () => {
    document
      .getElementById(HOW_IT_WORKS_ID)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const bentoCards = [
    { key: 'transparency' as const },
    { key: 'support' as const },
    { key: 'borderless' as const },
    { key: 'health' as const },
  ]

  return (
    <div className="flex min-h-screen flex-col bg-bg text-text">
      <header className="mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-5">
        <BrandMark />
        <div className="flex items-center gap-3">
          {/* Navigation, so a real <Link> (middle-click, "open in new tab", announced
              as a link) — styled with @clari/ui's button classes, since <Button>
              only renders a <button>. Depends on those class names: keep in sync
              with packages/ui/src/components.css (.clari-button*). No brand dot —
              it stays on the primary CTA. */}
          <Link to={signedIn ? '/app' : '/login'} className="clari-button clari-button--secondary">
            <span>
              {signedIn ? t('nav.dashboard', { ns: 'auth' }) : t('signin.title', { ns: 'auth' })}
            </span>
          </Link>
          <LanguageSwitcher />
          <ThemeToggle
            defaultMode="light"
            labels={{
              switchToLight: t('theme.switch_to_light'),
              switchToDark: t('theme.switch_to_dark'),
              switchToSystem: t('theme.switch_to_system'),
            }}
          />
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-8 px-5 pb-9">
        <section className="flex flex-col gap-5 pt-7">
          <h1 className="text-h1 font-semibold tracking-tight md:text-display-md">
            {t('landing.tagline')}
          </h1>
          <p className="max-w-2xl text-lg text-text-muted">{t('landing.subtitle')}</p>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button variant="primary" withSignatureDot onClick={() => navigate('/signup')}>
              {t('landing.cta_primary')}
            </Button>
            <Button variant="secondary" onClick={scrollToHowItWorks}>
              {t('landing.cta_secondary')}
            </Button>
          </div>
        </section>

        <section
          id={HOW_IT_WORKS_ID}
          aria-label={t('app.name')}
          className="grid grid-cols-1 gap-4 scroll-mt-5 md:grid-cols-2 lg:grid-cols-4"
        >
          {bentoCards.map((card) => (
            <Card
              key={card.key}
              variant="action"
              title={t(`bento.${card.key}.title`)}
              description={t(`bento.${card.key}.body`)}
            />
          ))}
        </section>
      </main>
      <Footer />
    </div>
  )
}
